import { LinearGradient } from "expo-linear-gradient";
import React, { useEffect, useState } from "react";
import { View, ActivityIndicator, Platform, Alert } from "react-native";

import { getScreenshotPersona, isScreenshotMode } from '../../lib/screenshotMode';
import { subscriptionStateManager } from '../features/monetization/services/subscriptionStateManager';
import { useUserProfile } from '../features/quiz-core/useStore';
import { supabase } from "../lib/supabase";
import OAuthCallback from "../shared/components/OAuthCallback";
import { useGuestUser } from '../shared/contexts/GuestUserContext';
import { syncFullStateToPGS } from '../shared/services/pgsAchievementBridge';
import { loadProgress } from '../shared/services/pgsCloudSaveService';
import * as playGamesService from '../shared/services/playGamesService';
import type { PGSCloudSavePayload } from '../shared/types/playGames';
import AuthScreen from "../shared/ui/AuthScreen";
import LandingScreen from "../shared/ui/LandingScreen";
import PGSRecoveryScreen from "../shared/ui/PGSRecoveryScreen";
import { uiText } from '../shared/utils/uiText';

/**
 * AuthGuard wraps children and renders them either:
 * - For authenticated users (signed in)
 * - For guest users (mobile only - playing without account)
 * - Otherwise shows LandingScreen -> AuthScreen flow
 */
export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const screenshotMode = isScreenshotMode();
  const screenshotPersona = getScreenshotPersona();
  const screenshotGuestMode = screenshotMode && screenshotPersona === 'guest';
  const [checking, setChecking] = useState(screenshotMode ? false : true);
  const [signedIn, setSignedIn] = useState<boolean | null>(screenshotMode ? !screenshotGuestMode : null);
  const [showLanding, setShowLanding] = useState(screenshotMode ? false : true);
  const [initialSessionChecked, setInitialSessionChecked] = useState(screenshotMode ? true : false);
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);
  const [allowGuestMode, setAllowGuestMode] = useState(screenshotGuestMode);
  const [pgsCloudSave, setPgsCloudSave] = useState<PGSCloudSavePayload | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const { startGuestSession, authState: guestAuthState } = useGuestUser();

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.location || !signedIn) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    if (!checkoutStatus) {
      return;
    }

    if (checkoutStatus === 'success') {
      subscriptionStateManager.refreshAllSubscriptionData().catch((error) => {
        console.warn('[AuthGuard] Failed to refresh subscription state after Checkout return:', error);
      });
      Alert.alert(
        uiText('checkoutCompleteTitle'),
        uiText('checkoutCompleteMessage')
      );
    } else if (checkoutStatus === 'cancelled') {
      Alert.alert(
        uiText('checkoutCancelledTitle'),
        uiText('checkoutCancelledMessage')
      );
    }

    params.delete('checkout');
    params.delete('plan');
    const query = params.toString();
    const cleanPath = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, document.title, cleanPath);
  }, [signedIn]);

  // PGS boot helper — fire-and-forget, silent only, never shows UI
  const bootPGS = (userId: string) => {
    if (Platform.OS !== 'android') return;
    playGamesService
      .initialize()
      .then(() => playGamesService.trySilentSignIn())
      .then((playerId) => {
        if (!playerId) return; // Not signed into Play Games — don't prompt
        // Only refresh EXISTING mappings — never creates new ones
        playGamesService.refreshExistingLink(userId, playerId).then((isLinked) => {
          if (isLinked) {
            syncFullStateToPGS(userId); // Layer 2 delta-based catch-up
          }
        });
      })
      .catch(() => {}); // Silent failure, never blocks app
  };

  useEffect(() => {
    if (screenshotMode) return;
    // Check if this is an OAuth callback (web only)
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const hasOAuthParams = urlParams.has('code') || urlParams.has('error') ||
                              window.location.pathname.includes('/auth/callback');

        if (hasOAuthParams) {
          console.log('🔍 [AuthGuard] OAuth callback detected - checking for existing session first');

          // Check if user is already signed in before showing OAuth callback screen
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
              console.log('✅ [AuthGuard] User already signed in, skipping OAuth callback screen');
              setSignedIn(true);
              setShowLanding(false);
              setChecking(false);
              setInitialSessionChecked(true);

              // Clean up URL
              window.history.replaceState({}, document.title, '/');
              return;
            } else {
              console.log('🔄 [AuthGuard] No existing session, showing OAuth callback screen');
              setIsOAuthCallback(true);
              setShowLanding(false);
              setChecking(false);
            }
          });
          return;
        }
      } catch (error) {
        console.warn('[AuthGuard] Error checking OAuth callback:', error);
      }
    }

    let lastSignedInTime = 0;
    const check = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setSignedIn(!!user);
      setChecking(false);
    };
    check();
    let sessionRestored = false;
    let fallbackTimeout: NodeJS.Timeout | undefined;
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      // Never log the session object: it holds the access and refresh tokens, and release builds keep console output.
      console.log('[AuthGuard] onAuthStateChange event:', event, session ? 'with session' : 'no session');
      if (event === 'INITIAL_SESSION') {
        if (session && session.user) {
          sessionRestored = true;
          setInitialSessionChecked(true);
          setSignedIn(true);
          setShowLanding(false);
          useUserProfile.getState().fetchProfile();
          bootPGS(session.user.id);
          console.log('[AuthGuard] INITIAL_SESSION with session');
        } else {
          // No session — check for PGS cloud save (Phase 2 recovery)
          if (Platform.OS === 'android') {
            playGamesService.initialize()
              .then(() => playGamesService.trySilentSignIn())
              .then(async (playerId) => {
                if (!playerId) return;
                const save = await loadProgress();
                if (save && save.supabaseUserId) {
                  setPgsCloudSave(save);
                  setShowRecovery(true);
                }
              })
              .catch(() => {});
          }
          fallbackTimeout = setTimeout(() => {
            if (!sessionRestored) {
              setInitialSessionChecked(true);
              setSignedIn(false);
              console.warn('[AuthGuard] Fallback: No session after INITIAL_SESSION');
            }
          }, 2000);
        }
      }
      if (event === 'SIGNED_IN') {
        if (fallbackTimeout) clearTimeout(fallbackTimeout);
        lastSignedInTime = Date.now();
        setInitialSessionChecked(true);
        setSignedIn(true);
        sessionRestored = true;
        setShowLanding(false);
        useUserProfile.getState().fetchProfile();
        if (session?.user?.id) {
          bootPGS(session.user.id);
        }
        console.log('[AuthGuard] SIGNED_IN event - user authenticated, hiding landing screen');
      }
      if (event === 'SIGNED_OUT') {
        // Double-check session before signing out to prevent false signouts.
        // Apple/Google OAuth on iPad can fire a spurious SIGNED_OUT right
        // after SIGNED_IN.  Re-check lastSignedInTime at verification time
        // (not just when the event was received) so a SIGNED_IN that arrives
        // during the delay window is never overwritten.
        const verifySignOut = async (attempt: number = 1, maxAttempts: number = 3) => {
          // Re-read the latest sign-in timestamp each time we verify,
          // not the stale snapshot from when the event was received.
          const recentSignIn = lastSignedInTime;
          const now = Date.now();

          // If a SIGNED_IN arrived in the last 5 seconds, this SIGNED_OUT is stale.
          if (recentSignIn > 0 && now - recentSignIn < 5000) {
            console.log('[AuthGuard] Ignoring stale SIGNED_OUT - SIGNED_IN occurred recently');
            return;
          }

          try {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (currentSession && currentSession.user) {
              console.log(`[AuthGuard] Valid session found despite SIGNED_OUT event (attempt ${attempt}), keeping user signed in`);
              setSignedIn(true);
              return;
            }

            // On first attempts, retry after a delay (iPad session propagation can be slow)
            if (attempt < maxAttempts) {
              console.log(`[AuthGuard] No session on attempt ${attempt}, retrying...`);
              setTimeout(() => verifySignOut(attempt + 1, maxAttempts), 1000);
              return;
            }

            // All attempts exhausted - proceed with signout
            setSignedIn(false);
            setInitialSessionChecked(true);
            sessionRestored = false;
            setShowLanding(true);
            if (fallbackTimeout) clearTimeout(fallbackTimeout);
            console.log('[AuthGuard] SIGNED_OUT event confirmed after all verification attempts');
          } catch (error) {
            console.error('[AuthGuard] Error checking session on SIGNED_OUT:', error);
            // On error, retry if attempts remaining
            if (attempt < maxAttempts) {
              setTimeout(() => verifySignOut(attempt + 1, maxAttempts), 1000);
            } else {
              setSignedIn(false);
              setInitialSessionChecked(true);
              sessionRestored = false;
              setShowLanding(true);
            }
          }
        };

        // Start verification after initial delay
        setTimeout(() => verifySignOut(1, 3), 1500);
      }
    });
    return () => {
      listener?.subscription.unsubscribe();
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
    };
  }, []);

  // When guest session ends (user wants to sign in), return to auth flow
  useEffect(() => {
    if (screenshotMode) return;
    if (allowGuestMode && guestAuthState !== 'guest') {
      console.log('[AuthGuard] Guest session ended — showing auth screen');
      setAllowGuestMode(false);
      setShowLanding(false); // Skip landing, go straight to auth screen
    }
  }, [guestAuthState, allowGuestMode]);

  const handleGetStarted = () => {
    setShowLanding(false);
  };

  const handlePlayAsGuest = async () => {
    // Only allow guest mode on mobile platforms
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      console.log('👤 [AuthGuard] Starting guest mode');
      await startGuestSession();
      setAllowGuestMode(true);
      setShowLanding(false);
    }
  };

  const handleBackToLanding = () => {
    setShowLanding(true);
    setAllowGuestMode(false);
  };

  const handleOAuthSuccess = () => {
    console.log('✅ [AuthGuard] OAuth success - user should be signed in');
    setIsOAuthCallback(false);
    setSignedIn(true);
    setShowLanding(false);
    useUserProfile.getState().fetchProfile();
  };

  const handleOAuthError = (error: string) => {
    console.error('❌ [AuthGuard] OAuth error:', error);
    setIsOAuthCallback(false);
    setSignedIn(false);
    setShowLanding(false); // Go to auth screen to show error
  };

  if (checking || !initialSessionChecked) {
    return (
      <View style={{ flex: 1 }} data-oid="w:npi_o">
        <LinearGradient
          colors={["#3B4CCA", "#4C63D2", "#5A73DA"]}
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
          data-oid="zcomg.w"
        >
          <ActivityIndicator size="large" color="#FFFFFF" data-oid="df_6__7" />
        </LinearGradient>
      </View>
    );
  }

  // Handle OAuth callback
  if (isOAuthCallback) {
    return (
      <OAuthCallback
        onSuccess={handleOAuthSuccess}
        onError={handleOAuthError}
      />
    );
  }

  // PGS Recovery Screen (Phase 2) — shown when cloud save exists, no session
  if (!signedIn && showRecovery && pgsCloudSave) {
    return (
      <PGSRecoveryScreen
        cloudSave={pgsCloudSave}
        onSignIn={() => {
          setShowRecovery(false);
          setShowLanding(false); // Go straight to auth screen
        }}
        onSkip={() => {
          setShowRecovery(false);
          setPgsCloudSave(null);
        }}
      />
    );
  }

  // Allow access if signed in OR guest mode is active
  if (!signedIn && !allowGuestMode) {
    if (showLanding) {
      return (
        <LandingScreen
          onGetStarted={handleGetStarted}
          onPlayAsGuest={handlePlayAsGuest}
          data-oid="wg4rk8d"
        />
      );
    } else {
      return (
        <AuthScreen
          onBack={handleBackToLanding}
          onPlayAsGuest={handlePlayAsGuest}
          data-oid="qo5whue"
        />
      );
    }
  }

  return <>{children}</>;
}
